import { useRef, useState } from "react";
import { UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ACCEPT, TYPES_DOCUMENT, YEARS } from "@/lib/dataroom";
import { cn } from "@/lib/utils";

export default function UploadZone({ onFiles, testPrefix = "" }) {
    const t = (id) => (testPrefix ? `${testPrefix}-${id}` : id);
    const inputRef = useRef(null);
    const [type, setType] = useState("");
    const [periode, setPeriode] = useState("");
    const [typePersonnalise, setTypePersonnalise] = useState("");
    const [drag, setDrag] = useState(false);
    const [busy, setBusy] = useState(false);

    async function send(fileList) {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        if (!type) {
            toast.warning("Choisissez d'abord le type de document", { description: "Le tag est obligatoire pour chaque dépôt." });
            return;
        }
        if (type === "autre" && !typePersonnalise.trim()) {
            toast.warning("Précisez le type de document", { description: "Obligatoire pour la catégorie « Autres »." });
            return;
        }
        setBusy(true);
        await onFiles(files, type, periode, type === "autre" ? typePersonnalise.trim() : "");
        setBusy(false);
        setType("");
        setPeriode("");
        setTypePersonnalise("");
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Type de document <span className="text-destructive">*</span> :</span>
                <Select value={type} onValueChange={setType}>
                    <SelectTrigger data-testid={t("document-type-select")} className={cn("h-9 w-64 bg-card", !type && "border-primary/40")}>
                        <SelectValue placeholder="Sélectionner…" />
                    </SelectTrigger>
                    <SelectContent>
                        {TYPES_DOCUMENT.map((t) => (
                            <SelectItem key={t.value} value={t.value} data-testid={`document-type-${t.value}`}>
                                {t.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground ml-2">Exercice :</span>
                <Select value={periode} onValueChange={(v) => setPeriode(v === "auto" ? "" : v)}>
                    <SelectTrigger data-testid={t("document-periode-select")} className="h-9 w-56 bg-card">
                        <SelectValue placeholder="Auto (depuis le nom du fichier)" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="auto" data-testid="document-periode-auto">Auto (depuis le nom du fichier)</SelectItem>
                        {YEARS.map((y) => <SelectItem key={y} value={String(y)} data-testid={`document-periode-${y}`}>{y}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
            {type === "autre" && (
                <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">Précisez le type de document <span className="text-destructive">*</span> :</span>
                    <Input
                        data-testid={t("document-type-personnalise-input")}
                        value={typePersonnalise}
                        onChange={(e) => setTypePersonnalise(e.target.value)}
                        placeholder="ex. Contrat de bail, Grand livre, Attestation URSSAF"
                        maxLength={120}
                        className="h-9 w-96 bg-card border-primary/40"
                    />
                </div>
            )}

            <div
                data-testid={t("upload-dropzone")}
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => { e.preventDefault(); setDrag(false); send(e.dataTransfer.files); }}
                className={cn(
                    "rounded-md border border-dashed px-6 py-10 flex flex-col items-center text-center cursor-pointer transition-colors",
                    drag ? "border-primary bg-accent" : "border-border bg-transparent hover:bg-accent/40",
                    (busy || !type) && "opacity-60",
                    busy && "pointer-events-none"
                )}
            >
                <UploadCloud className="h-6 w-6 text-primary mb-3" strokeWidth={1.5} />
                <p className="text-sm text-foreground">
                    {busy ? "Dépôt en cours…" : !type ? "Sélectionnez le type de document ci-dessus pour déposer" : type === "autre" && !typePersonnalise.trim() ? "Précisez le type de document pour déposer" : "Glissez vos fichiers ici ou cliquez pour parcourir"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">Dépôt multiple par glisser-déposer · PDF (liasses, bilans), FEC (CSV / TXT), Excel, Word, images — 50 Mo max par fichier · l&apos;année est détectée depuis le nom du fichier</p>
                <input
                    ref={inputRef}
                    data-testid={t("upload-file-input")}
                    type="file"
                    multiple
                    accept={ACCEPT}
                    className="hidden"
                    onChange={(e) => { send(e.target.files); e.target.value = ""; }}
                />
            </div>
        </div>
    );
}
